import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Box,
    Chip,
    CircularProgress,
    Alert,
    TextField,
    MenuItem,
    Grid,
    Card,
    CardContent,
    TablePagination
} from '@mui/material';
import { Sync as SyncIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import apiClient from '../services/apiClient';

interface Site {
    id: string;
    code: string;
    name: string;
    description: string;
    authority_id: string;
    hospital_id: string;
    asl_id: string;
    type: string;
    enabled: boolean;
    active: boolean;
    disconnected: boolean;
    address: string;
    city: string;
    province: string;
    cap: string;
    phone_number: string;
    email: string;
    created_at: string;
    updated_at: string;
}

interface Hospital {
    id: string;
    name: string;
}

interface Authority {
    id: string;
    name: string;
}

const SitesPage: React.FC = () => {
    console.log('🏢 SitesPage: Component mounted/rendered');
    
    const [sites, setSites] = useState<Site[]>([]);
    const [hospitals, setHospitals] = useState<Hospital[]>([]);
    const [authorities, setAuthorities] = useState<Authority[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [syncing, setSyncing] = useState(false);
    const [selectedHospital, setSelectedHospital] = useState('');
    
    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);

    const fetchSites = async () => {
        console.log('🏢 SitesPage: fetchSites called');
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                limit: rowsPerPage.toString(),
                offset: (page * rowsPerPage).toString()
            });
            
            if (selectedHospital) {
                params.append('hospitalId', selectedHospital);
            }

            console.log('🏢 SitesPage: Making API call to:', `/api/recup-sync/sites?${params}`);
            const response = await apiClient.get(`/recup-sync/sites?${params}`);
            console.log('🏢 SitesPage: API response received:', response.data);
            
            if (response.data.success) {
                setSites(response.data.data);
                setTotalCount(response.data.pagination.total);
                console.log('🏢 SitesPage: Sites data set successfully');
            } else {
                setError(response.data.error || 'Errore nel recupero dei sites');
                console.error('🏢 SitesPage: API returned error:', response.data.error);
            }
        } catch (err: any) {
            console.error('🏢 SitesPage: API call failed:', err);
            setError(err.response?.data?.error || err.message || 'Errore nel recupero dei sites');
        } finally {
            setLoading(false);
        }
    };

    const fetchHospitals = async () => {
        try {
            const response = await apiClient.get('/recup-sync/hospitals?limit=1000');
            if (response.data.success) {
                setHospitals(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching hospitals:', err);
        }
    };

    const fetchAuthorities = async () => {
        try {
            const response = await apiClient.get('/recup-sync/authorities?limit=1000');
            if (response.data.success) {
                setAuthorities(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching authorities:', err);
        }
    };

    const handleSync = async () => {
        setSyncing(true);
        setError(null);
        try {
            const response = await apiClient.post('/recup-sync/sync/sites');
            if (response.data.success) {
                await fetchSites();
                alert(`Sincronizzazione completata con successo!\nCreati: ${response.data.created}\nAggiornati: ${response.data.updated}`);
            } else {
                setError(response.data.error || 'Errore nella sincronizzazione');
            }
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Errore nella sincronizzazione');
        } finally {
            setSyncing(false);
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getStatusChip = (site: Site) => {
        if (site.disconnected) {
            return <Chip label="Disconnesso" color="error" size="small" />;
        }
        if (!site.active) {
            return <Chip label="Inattivo" color="warning" size="small" />;
        }
        if (!site.enabled) {
            return <Chip label="Disabilitato" color="default" size="small" />;
        }
        return <Chip label="Attivo" color="success" size="small" />;
    };

    const getHospitalName = (hospitalId: string) => {
        const hospital = hospitals.find(h => h.id === hospitalId);
        return hospital?.name || hospitalId;
    };

    const getAuthorityName = (authorityId: string) => {
        const authority = authorities.find(a => a.id === authorityId);
        return authority?.name || authorityId;
    };

    useEffect(() => {
        console.log('🏢 SitesPage: useEffect for hospitals and authorities triggered');
        fetchHospitals();
        fetchAuthorities();
    }, []);

    useEffect(() => {
        console.log('🏢 SitesPage: useEffect for sites triggered with:', { page, rowsPerPage, selectedHospital });
        fetchSites();
    }, [page, rowsPerPage, selectedHospital]);

    console.log('🏢 SitesPage: Rendering component');
    
    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
                Gestione Sites
            </Typography>

            {/* Statistiche */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Totale Sites
                            </Typography>
                            <Typography variant="h5">
                                {totalCount}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Sites Attivi
                            </Typography>
                            <Typography variant="h5">
                                {sites.filter(s => s.active && s.enabled && !s.disconnected).length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Sites Hospitals
                            </Typography>
                            <Typography variant="h5">
                                {hospitals.length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Filtro Attivo
                            </Typography>
                            <Typography variant="h5">
                                {selectedHospital ? 'Sì' : 'No'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Controlli */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                        select
                        label="Filtra per Hospital"
                        value={selectedHospital}
                        onChange={(e) => {
                            setSelectedHospital(e.target.value);
                            setPage(0);
                        }}
                        sx={{ minWidth: 200 }}
                        size="small"
                    >
                        <MenuItem value="">Tutti gli hospitals</MenuItem>
                        {hospitals.map((hospital) => (
                            <MenuItem key={hospital.id} value={hospital.id}>
                                {hospital.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    
                    <Button
                        variant="outlined"
                        onClick={fetchSites}
                        disabled={loading}
                        startIcon={<RefreshIcon />}
                    >
                        Aggiorna
                    </Button>
                    
                    <Button
                        variant="contained"
                        onClick={handleSync}
                        disabled={syncing}
                        startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
                        color="primary"
                    >
                        {syncing ? 'Sincronizzazione...' : 'Sincronizza da RECUP'}
                    </Button>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Tabella Sites */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Codice</TableCell>
                                <TableCell>Nome</TableCell>
                                <TableCell>Descrizione</TableCell>
                                <TableCell>Hospital</TableCell>
                                <TableCell>Authority</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell>Stato</TableCell>
                                <TableCell>Ubicazione</TableCell>
                                <TableCell>Contatti</TableCell>
                                <TableCell>Creato</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell style={{ textAlign: 'center' }} colSpan={11}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : sites.length === 0 ? (
                                <TableRow>
                                    <TableCell style={{ textAlign: 'center' }} colSpan={11}>
                                        Nessun site trovato
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sites.map((site) => (
                                    <TableRow key={site.id}>
                                        <TableCell>{site.id}</TableCell>
                                        <TableCell>{site.code}</TableCell>
                                        <TableCell>{site.name}</TableCell>
                                        <TableCell>{site.description}</TableCell>
                                        <TableCell>{getHospitalName(site.hospital_id)}</TableCell>
                                        <TableCell>{getAuthorityName(site.authority_id)}</TableCell>
                                        <TableCell>{site.type}</TableCell>
                                        <TableCell>{getStatusChip(site)}</TableCell>
                                        <TableCell>
                                            {site.address && (
                                                <div>
                                                    <div>{site.address}</div>
                                                    <div>{site.city} {site.cap} ({site.province})</div>
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {(site.phone_number || site.email) && (
                                                <div>
                                                    {site.phone_number && <div>📞 {site.phone_number}</div>}
                                                    {site.email && <div>✉️ {site.email}</div>}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {new Date(site.created_at).toLocaleDateString()}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Righe per pagina:"
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`
                    }
                />
            </Paper>
        </Container>
    );
};

export default SitesPage;